import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { marked } from "marked";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { LoadingPanel } from "../../components/LoadingPanel";
import { SectionCardHeader } from "../../components/ChartCard";
import {
  DOC_LIST_SQL, QA_DB, buildDocDetailSql,
  mapDocListRows, mapDocDetailRows,
} from "./sql";
import type { DocListRow, DocDetailRow } from "./sql";
import { QA_LABELS, QA_COLORS } from "./qaAnalysis";
import type { QAType } from "./qaAnalysis";
import { annotateOutputAgainstTemplate, annotateTemplate } from "./outputAnnotations";
import { DocRow } from "./DocRow";

const QA_TYPES: QAType[] = ["fixed", "fixed-variable", "fixed-conditional", "variable"];

function fmtK(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export function DocumentQA() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [templateFilter, setTemplateFilter] = useState<string>("all");

  const docList = useAdminQuery(DOC_LIST_SQL, [], QA_DB);

  const docListRows = useMemo<DocListRow[]>(() => {
    if (!docList.data) return [];
    return mapDocListRows(docList.data.columns, docList.data.rows as unknown[][]);
  }, [docList.data]);

  const templateOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of docListRows) {
      if (r.template_external_id && r.template_name) {
        seen.set(r.template_external_id, r.template_name);
      }
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [docListRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docListRows.filter((r) => {
      if (templateFilter !== "all" && r.template_external_id !== templateFilter) return false;
      if (q && !r.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [docListRows, search, templateFilter]);

  const docsByTemplate = useMemo(() => {
    const m = new Map<string, DocListRow[]>();
    for (const r of docListRows) {
      if (!r.template_external_id) continue;
      const arr = m.get(r.template_external_id) ?? [];
      arr.push(r);
      m.set(r.template_external_id, arr);
    }
    return m;
  }, [docListRows]);

  const docDetailSql = useMemo(() => buildDocDetailSql(selectedId), [selectedId]);
  const docDetail = useAdminQuery(docDetailSql, [], QA_DB);

  const docDetailRow = useMemo<DocDetailRow | null>(() => {
    if (!docDetail.data) return null;
    const rows = mapDocDetailRows(docDetail.data.columns, docDetail.data.rows as unknown[][]);
    return rows[0] ?? null;
  }, [docDetail.data]);

  const selectedDoc = useMemo(
    () => docListRows.find((r) => r.doc_id === selectedId) ?? null,
    [docListRows, selectedId],
  );

  if (selectedId) {
    return (
      <CompareView
        doc={selectedDoc}
        detail={docDetailRow}
        isLoading={docDetail.isLoading}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <>
      <Topbar crumbs={["Dashboards", "Document QA"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Operations · Stage QA company</div>
            <h1>Document QA</h1>
            <p className="lede">
              Each row shows a heuristic 0-100 QA score plus, when available, the verdict
              from your most recent local /template-qa run for that template (PASS / FAIL
              with version). Click the score to see the breakdown. Click the row to open
              the three-pane source / output / template-prompt comparison. Run /template-qa
              locally in Claude Code, commit the outputs/template-qa folder, and the
              reports surface here per-doc.
            </p>
          </div>
        </div>

        {docList.isLoading && docListRows.length === 0 ? (
          <LoadingPanel />
        ) : docList.isError ? (
          <p style={{ color: "var(--fg-danger)" }}>Failed to load documents.</p>
        ) : (
          <div className="card" style={{ marginTop: 16 }}>
            <SectionCardHeader
              title="QA Documents"
              subtitle={`${filteredRows.length} of ${docListRows.length} documents. Click a row to open the 3-pane comparison.`}
              headerExtra={
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={templateFilter}
                    onChange={(e) => setTemplateFilter(e.target.value)}
                    style={{
                      fontSize: 12, padding: "4px 8px",
                      border: "1px solid var(--border-default)", borderRadius: 4,
                      background: "var(--bg-1)", color: "var(--fg-1)", maxWidth: 240,
                    }}
                  >
                    <option value="all">All templates</option>
                    {templateOptions.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                  <input
                    type="search"
                    placeholder="Filter by name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      fontSize: 12, padding: "4px 8px",
                      border: "1px solid var(--border-default)", borderRadius: 4,
                      background: "var(--bg-1)", color: "var(--fg-1)", width: 180,
                    }}
                  />
                </div>
              }
            />
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th>Document name</th>
                      <th>Template</th>
                      <th>Status</th>
                      <th>QA Score</th>
                      <th>Created</th>
                      <th className="num">Output</th>
                      <th>Template ID</th>
                      <th>Document ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <DocRow
                        key={r.doc_id}
                        row={r}
                        siblings={r.template_external_id ? docsByTemplate.get(r.template_external_id) ?? [] : []}
                        expanded={expandedDocId === r.doc_id}
                        onToggleExpand={(id) => setExpandedDocId((prev) => (prev === id ? null : id))}
                        onOpenCompare={(id) => setSelectedId(id)}
                        colSpan={8}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ---- compare view ----

function CompareView({
  doc,
  detail,
  isLoading,
  onBack,
}: {
  doc: DocListRow | null;
  detail: DocDetailRow | null;
  isLoading: boolean;
  onBack: () => void;
}) {
  const [showQA, setShowQA] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [syncScroll, setSyncScroll] = useState(true);
  const [widths, setWidths] = useState([33.33, 33.33, 33.34]);
  const [dragging, setDragging] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const middleRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  useEffect(() => {
    if (!syncScroll) return;
    const panes = [leftRef.current, middleRef.current, rightRef.current].filter(Boolean) as HTMLDivElement[];
    if (panes.length < 2) return;
    const handlers = panes.map((source, i) => {
      const handler = () => {
        if (syncing.current) return;
        syncing.current = true;
        const pct = source.scrollTop / Math.max(1, source.scrollHeight - source.clientHeight);
        panes.forEach((target, j) => {
          if (j !== i) target.scrollTop = pct * (target.scrollHeight - target.clientHeight);
        });
        syncing.current = false;
      };
      source.addEventListener("scroll", handler);
      return { source, handler };
    });
    return () => handlers.forEach(({ source, handler }) => source.removeEventListener("scroll", handler));
  }, [syncScroll]);

  const annotatedTemplate = useMemo(() => {
    if (!detail?.template_content) return null;
    return annotateTemplate(detail.template_content);
  }, [detail?.template_content]);

  const annotatedOutput = useMemo(() => {
    if (!detail?.doc_content || !detail?.template_content) return null;
    return annotateOutputAgainstTemplate(detail.doc_content, detail.template_content);
  }, [detail?.doc_content, detail?.template_content]);

  const sourceHtml = useMemo(() => {
    if (!detail?.source_markdown) return null;
    return marked.parse(detail.source_markdown, { async: false }) as string;
  }, [detail?.source_markdown]);

  const qaSummary = annotatedOutput?.summary ?? null;

  function startResize(handleIndex: number, e: ReactMouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidths = [...widths];
    const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 1;
    setDragging(handleIndex);
    const onMove = (me: MouseEvent) => {
      const dPct = ((me.clientX - startX) / containerWidth) * 100;
      const total = startWidths[handleIndex]! + startWidths[handleIndex + 1]!;
      const MIN = 10;
      const a = Math.min(total - MIN, Math.max(MIN, startWidths[handleIndex]! + dPct));
      const next = [...startWidths];
      next[handleIndex] = a;
      next[handleIndex + 1] = total - a;
      setWidths(next);
    };
    const onUp = () => {
      setDragging(null);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const templateId = detail?.template_id ?? doc?.template_external_id ?? null;
  const docId = detail?.doc_id ?? doc?.doc_id ?? null;
  const templateContent = detail?.template_content ?? null;
  const docContent = detail?.doc_content ?? null;
  const displayedTemplate = showQA ? (annotatedTemplate?.html ?? templateContent) : templateContent;
  const displayedDoc = showQA ? (annotatedOutput?.html ?? docContent) : docContent;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 20px",
        borderBottom: "1px solid var(--border-default)",
        background: "var(--bg-2)", flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            border: "1px solid var(--border-default)", borderRadius: 4,
            background: "var(--bg-1)", color: "var(--fg-2)",
            fontSize: 12, padding: "4px 10px", cursor: "pointer", flexShrink: 0,
          }}
        >
          <i className="ti ti-arrow-left" style={{ fontSize: 13 }} />
          Back to list
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {doc?.name ?? "Document"}
          </div>
          {detail?.template_name && (
            <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>
              Template: {detail.template_name}
            </div>
          )}
        </div>

        {/* QA legend */}
        {qaSummary && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
            {QA_TYPES.map((t) => (
              <span key={t} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--fg-2)" }}>
                <span style={{
                  display: "inline-block", width: 10, height: 10, borderRadius: 2,
                  background: QA_COLORS[t].bg === "transparent" ? "var(--border-default)" : QA_COLORS[t].bg,
                  border: `1px solid ${QA_COLORS[t].border === "transparent" ? "var(--border-default)" : QA_COLORS[t].border}`,
                }} />
                {QA_LABELS[t]}
                <span style={{ color: "var(--fg-3)" }}>({qaSummary[t]})</span>
              </span>
            ))}
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--fg-2)" }}>
              <span style={{
                display: "inline-block", width: 10, height: 10, borderRadius: 2,
                background: "transparent",
                border: "2px dashed #DC6A4C",
              }} />
              Deviation
              <span style={{ color: qaSummary.deviations > 0 ? "#DC6A4C" : "var(--fg-3)", fontWeight: qaSummary.deviations > 0 ? 600 : 400 }}>
                ({qaSummary.deviations})
              </span>
            </span>
          </div>
        )}

        {/* Controls */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setSyncScroll(!syncScroll)}
            style={{
              fontSize: 11, padding: "3px 8px",
              border: "1px solid var(--border-default)", borderRadius: 3,
              background: syncScroll ? "var(--bg-3, #e8e8e8)" : "var(--bg-1)",
              color: syncScroll ? "var(--fg-1)" : "var(--fg-3)",
              cursor: "pointer",
            }}
          >
            <i className="ti ti-lock" style={{ fontSize: 11, marginRight: 4 }} />
            {syncScroll ? "Scroll synced" : "Scroll free"}
          </button>
          <button
            type="button"
            onClick={() => setShowQA(!showQA)}
            style={{
              fontSize: 11, padding: "3px 8px",
              border: "1px solid var(--border-default)", borderRadius: 3,
              background: showQA ? "rgba(139,92,246,0.12)" : "var(--bg-1)",
              color: showQA ? "#8b5cf6" : "var(--fg-3)",
              cursor: "pointer",
            }}
          >
            QA highlights {showQA ? "on" : "off"}
          </button>
          <button
            type="button"
            onClick={() => setShowRaw(!showRaw)}
            style={{
              fontSize: 11, padding: "3px 8px",
              border: "1px solid var(--border-default)", borderRadius: 3,
              background: "var(--bg-1)", color: "var(--fg-2)",
              cursor: "pointer",
            }}
          >
            {showRaw ? "Rendered" : "Raw HTML"}
          </button>
        </div>
      </div>

      {/* Three panes */}
      <div ref={containerRef} style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Pane 1: Licensee source document (markdown converted from the uploaded .docx) */}
        <Pane
          ref={leftRef}
          width={widths[0]!}
          label={detail?.source_name ? `Licensee Source: ${detail.source_name}` : "Licensee Source Document"}
          idLabel="Template ID"
          id={templateId}
        >
          {isLoading ? (
            <PaneLoading />
          ) : !sourceHtml ? (
            <PaneEmpty>No source document found for this template.</PaneEmpty>
          ) : showRaw ? (
            <RawView content={detail?.source_markdown ?? ""} />
          ) : (
            <RenderedView content={sourceHtml} />
          )}
        </Pane>

        <div
          className={`drag-handle${dragging === 0 ? " is-dragging" : ""}`}
          onMouseDown={(e: ReactMouseEvent<HTMLDivElement>) => startResize(0, e)}
        />

        {/* Pane 2: Document output with QA annotations */}
        <Pane
          ref={middleRef}
          width={widths[1]!}
          label="Document Output"
          idLabel="Document ID"
          id={docId}
        >
          {isLoading ? (
            <PaneLoading />
          ) : !displayedDoc ? (
            <PaneEmpty>No document content.</PaneEmpty>
          ) : showRaw ? (
            <RawView content={displayedDoc} />
          ) : (
            <RenderedView content={displayedDoc} />
          )}
        </Pane>

        <div
          className={`drag-handle${dragging === 1 ? " is-dragging" : ""}`}
          onMouseDown={(e: ReactMouseEvent<HTMLDivElement>) => startResize(1, e)}
        />

        {/* Pane 3: Template prompt with QA annotations (the prompt built from the source doc) */}
        <Pane
          ref={rightRef}
          width={widths[2]!}
          label="Document Template Prompt"
          idLabel="Template ID"
          id={templateId}
        >
          {isLoading ? (
            <PaneLoading />
          ) : !displayedTemplate ? (
            <PaneEmpty>No template linked to this document.</PaneEmpty>
          ) : showRaw ? (
            <RawView content={displayedTemplate} />
          ) : (
            <RenderedView content={displayedTemplate} />
          )}
        </Pane>
      </div>
    </div>
  );
}

// ---- primitives ----

const Pane = forwardRef<
  HTMLDivElement,
  { width: number; label: string; id: string | null; idLabel: string; children: ReactNode }
>(function Pane({ width, label, id, idLabel, children }, ref) {
  return (
    <div style={{ width: `${width}%`, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{
        padding: "6px 16px 8px",
        borderBottom: "1px solid var(--border-default)",
        background: "var(--bg-2)", flexShrink: 0,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--fg-3)" }}>
          {label}
        </div>
        <div style={{ fontSize: 10, color: "var(--fg-3)", fontFamily: "var(--font-mono)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <span style={{ marginRight: 4 }}>{idLabel}:</span>{id ?? "—"}
        </div>
      </div>
      <div ref={ref} style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        {children}
      </div>
    </div>
  );
});

function RenderedView({ content }: { content: string }) {
  return (
    // Internal template/document HTML; no user-controlled input reaches this.
    <div
      dangerouslySetInnerHTML={{ __html: content }}
      style={{ maxWidth: 760, fontSize: 14, lineHeight: 1.75, color: "var(--fg-1)" }}
    />
  );
}

function RawView({ content }: { content: string }) {
  return (
    <pre style={{
      margin: 0, fontSize: 12, lineHeight: 1.6,
      whiteSpace: "pre-wrap", wordBreak: "break-word",
      color: "var(--fg-2)", fontFamily: "var(--font-mono)",
    }}>
      {content}
    </pre>
  );
}

function PaneLoading() {
  return <span style={{ fontSize: 13, color: "var(--fg-3)" }}>Loading…</span>;
}

function PaneEmpty({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 13, color: "var(--fg-3)" }}>{children}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === "complete" || status === "completed"
      ? "var(--color-jade, #1E6D5B)"
      : status === "failed"
      ? "var(--color-coral-hover, #DC6A4C)"
      : "var(--fg-3)";
  return (
    <span style={{ fontSize: 11, color, fontWeight: 500, textTransform: "capitalize" }}>
      {status}
    </span>
  );
}

function IdCell({ id }: { id: string | null }) {
  return (
    <td style={{ fontSize: 10, color: "var(--fg-3)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
      {id ?? "—"}
    </td>
  );
}
